export const AI_STRINGS = {
  SECTION_TITLE: 'AI Assistants',
  CLAUDE_DESCRIPTION: 'Ask AI about shipment status, tracking history, and refresh tracking using natural language.',
  BUTTON_CONNECT: 'Connect to Claude',
  BUTTON_DISCONNECT: 'Disconnect',
  BUTTON_CANCEL: 'Cancel',
  BADGE_CONNECTED: 'Connected',
  MODAL_TITLE: 'Disconnect Claude?',
  MODAL_BODY: "Claude will lose access to your shop's shipment data. You can reconnect anytime.",
  ERROR_LOAD_FAILED: 'Unable to load AI assistants. Try again.',
  ERROR_DISCONNECT_FAILED: 'Failed to disconnect. Try again.',
  CONNECTED_ON: (date: string) => `Connected on ${date}`,
  LAST_USED: (time: string) => `Last used ${time}`,
  NEVER_USED: 'Never used',
} as const;

// Official Anthropic custom-connector prefill deep-link (works for both Claude.ai web
// and Claude Desktop, which share the claude.ai auth flow). The `modal` param tells
// claude.ai to auto-open the "Add custom connector" dialog with name + URL prefilled.
const CLAUDE_CONNECTOR_BASE = 'https://claude.ai/customize/connectors';
const CONNECTOR_NAME = 'Synctrack Order Tracking (Trakow)';

export const buildClaudeConnectorUrl = (): string => {
  // Public MCP endpoint goes through the `/s/api/` gateway path used across the app
  // (see fetchBase.ts) — e.g. https://apps.trackordernow.com/s/api/mcp in production.
  const mcpUrl = `${process.env.REACT_APP_BASE_URL}/s/api/mcp`;
  const params = new URLSearchParams({
    modal: 'add-custom-connector',
    connectorName: CONNECTOR_NAME,
    connectorUrl: mcpUrl,
  });
  return `${CLAUDE_CONNECTOR_BASE}?${params.toString()}`;
};

// Public MCP endpoint shown to merchants in the manual-setup cards (read-only field
// they copy into ChatGPT, Perplexity, Grok, or any other MCP-compatible client).
export const MCP_URL_DISPLAY = `${process.env.REACT_APP_BASE_URL}/s/api/mcp`;

// Literal-union subset for providers that require manual connector setup.
// Note: this is intentionally narrower than `BrandAvatar`'s `Brand` type (5 values
// including 'claude'); the two are parallel — Claude has its own one-click flow.
export type ProviderBrand = 'chatgpt' | 'perplexity' | 'grok' | 'other';

export interface IProviderConfig {
  key: ProviderBrand;
  brand: ProviderBrand;
  title: string;
  subtitle?: string;
  description?: string;
}

export const PROVIDERS_MANUAL: IProviderConfig[] = [
  { key: 'chatgpt', brand: 'chatgpt', title: 'ChatGPT', subtitle: 'by OpenAI' },
  { key: 'perplexity', brand: 'perplexity', title: 'Perplexity' },
  { key: 'grok', brand: 'grok', title: 'Grok', subtitle: 'by xAI' },
  {
    key: 'other',
    brand: 'other',
    title: 'Other MCP-compatible AI',
    description: 'Mistral Le Chat, Cursor, Cline, Continue, and more. If unsure, contact support.',
  },
];

export interface IGuideConfig {
  title: string;
  intro: string;
  steps: string[];
  officialGuide: { label: string; url: string };
  showSupport?: boolean;
}

export const GUIDES: Record<ProviderBrand, IGuideConfig> = {
  chatgpt: {
    title: 'How to connect ChatGPT',
    intro:
      'ChatGPT calls them "apps" (formerly "connectors"). Available on Plus, Pro, and Enterprise plans. Developer Mode must be enabled first — workspace admins/owners only.',
    steps: [
      'In ChatGPT, go to Settings → Apps & Connectors → Advanced settings',
      'Toggle Developer Mode on',
      'Click Create, paste the URL above, and give the connector a name',
      'On the Trakow authorization page, enter your Shopify store domain and click Authorize',
    ],
    officialGuide: {
      label: 'Developer mode and MCP apps in ChatGPT — OpenAI Help Center',
      url: 'https://help.openai.com/en/articles/12584461-developer-mode-and-mcp-apps-in-chatgpt-beta',
    },
  },
  perplexity: {
    title: 'How to connect Perplexity',
    intro: 'Custom connectors launched in March 2026. Available on Pro, Max, and Enterprise plans.',
    steps: [
      'In Perplexity, click your profile icon (bottom-left) → Settings → Connectors',
      'Click + Custom connector in the top-right, then select Remote',
      'Paste the URL above into the Server URL field and set authentication to OAuth',
      'Acknowledge the "I understand custom connectors can introduce risks" notice, then click Add',
      'Scope as Individual (private to you) or Organization (Enterprise admins can share org-wide)',
      'On the Trakow authorization page, enter your Shopify store domain and click Authorize',
    ],
    officialGuide: {
      label: 'Custom connectors launch — Perplexity Changelog (March 13, 2026)',
      url: 'https://www.perplexity.ai/changelog/what-we-shipped---march-13-2026',
    },
  },
  grok: {
    title: 'How to connect Grok',
    intro: 'Available on X Premium and SuperGrok plans across Grok Web, iOS, and Android.',
    steps: [
      'In Grok, open Settings → Connectors',
      'Click New Connector → Custom',
      'Paste the URL above',
      'On the Trakow authorization page, enter your Shopify store domain and click Authorize',
    ],
    officialGuide: {
      label: 'Connectors — xAI Docs',
      url: 'https://docs.x.ai/grok/connectors',
    },
  },
  other: {
    title: 'Connect another AI tool',
    intro:
      'Trakow works with any AI client that supports custom MCP connectors — including Mistral Le Chat, Cursor, Cline, Continue, and more. The exact setup steps vary by client.',
    steps: [
      'In your AI client, find the custom-connector or MCP setup option',
      'Paste the URL above into the MCP server URL field',
      'Choose OAuth as the authentication method if asked',
      'On the Trakow authorization page, enter your Shopify store domain and click Authorize',
    ],
    officialGuide: {
      label: 'Browse all MCP-compatible clients — Model Context Protocol',
      url: 'https://modelcontextprotocol.io/clients',
    },
    showSupport: true,
  },
};
